/**
 * DraggablePhoto Component
 * 
 * Individual photo in the gallery that can be dragged to layout slots.
 * Provides visual feedback during drag operations including hover indicator and reduced opacity.
 * 
 * Requirements: 5.1, 5.2, 7.1, 7.3
 */

import { useDrag } from 'react-dnd';
import { DRAG_TYPES, type DraggedPhoto } from '../types/drag-drop';
import type { EditPhotoMinimal } from '../types/edit-session';

export interface DraggablePhotoProps {
  /** Photo object to be dragged */
  photo: EditPhotoMinimal;
  /** Whether this photo is currently placed in the layout */
  isPlaced?: boolean;
  /** Optional callback when drag starts */
  onDragStart?: (photoId: string) => void;
  /** Optional callback when drag ends */
  onDragEnd?: (photoId: string, didDrop: boolean) => void;
}

/**
 * DraggablePhoto component - Drag source for photos in the gallery
 */
export function DraggablePhoto({ 
  photo, 
  isPlaced = false,
  onDragStart,
  onDragEnd,
}: DraggablePhotoProps) {
  /**
   * handleDragEnd - Called when drag operation ends
   * Requirement 5.5: Handle drag cancellation if dropped outside valid targets
   */
  const handleDragEnd = (_item: DraggedPhoto, monitor: any) => {
    const didDrop = monitor.didDrop();
    console.log(`[DraggablePhoto] Drag ended for photo ${photo.photoId}, didDrop: ${didDrop}`);
    onDragEnd?.(photo.photoId, didDrop);
    
    if (!didDrop) {
      console.log(`[DraggablePhoto] Photo ${photo.photoId} was not dropped on a valid target`);
    }
  };

  // Set up drag source using react-dnd
  const [{ isDragging }, drag] = useDrag<
    DraggedPhoto,
    unknown,
    { isDragging: boolean }
  >(() => ({
    type: DRAG_TYPES.PHOTO,
    item: () => {
      // Called when drag starts (Requirement 5.1)
      console.log(`[DraggablePhoto] Drag started for photo ${photo.photoId}`);
      onDragStart?.(photo.photoId);
      
      return {
        photoId: photo.photoId,
        sequence: photo.sequence,
      };
    },
    end: handleDragEnd,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }), [photo.photoId, photo.sequence, onDragStart, onDragEnd]);

  // Create object URL for photo display
  const photoUrl = URL.createObjectURL(photo.imageBlob);

  return (
    <div
      ref={drag}
      role="button"
      tabIndex={0}
      aria-label={`Photo ${photo.sequence + 1}${isPlaced ? ', currently placed in layout' : ''}, drag to place in layout`}
      aria-grabbed={isDragging}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          // Keyboard users can press Enter/Space to select photo for placement
          console.log(`[DraggablePhoto] Keyboard selection for photo ${photo.photoId}`);
        }
      }}
      className={`
        relative
        w-full
        aspect-[3/4]
        rounded-lg
        overflow-hidden
        transition-all
        duration-300
        ease-in-out
        cursor-move
        focus:outline-none
        focus:ring-4
        focus:ring-yellow-500
        focus:ring-offset-2
        ${isDragging ? 'opacity-50 scale-95 shadow-2xl' : 'opacity-100 shadow-md'}
        ${!isDragging ? 'hover:ring-4 hover:ring-yellow-400 hover:scale-105 hover:shadow-xl' : ''}
        ${isPlaced ? 'ring-2 ring-blue-400' : ''}
      `}
      data-testid={`draggable-photo-${photo.photoId}`}
      data-photo-id={photo.photoId}
      data-is-dragging={isDragging}
      data-is-placed={isPlaced}
    >
      {/* Photo image */}
      <img
        src={photoUrl}
        alt={`Photo ${photo.sequence + 1}`}
        className="w-full h-full object-cover"
        data-testid={`draggable-photo-image-${photo.photoId}`}
      />

      {/* Hover indicator overlay */}
      {!isDragging && (
        <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-10 transition-all duration-300 ease-in-out pointer-events-none flex items-center justify-center group">
          <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 ease-in-out transform scale-75 group-hover:scale-100">
            <svg
              className="w-10 h-10 text-white drop-shadow-lg"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
              />
            </svg>
          </div>
        </div>
      )}

      {/* Dragging indicator */}
      {isDragging && (
        <div className="absolute inset-0 bg-yellow-400 bg-opacity-30 pointer-events-none flex items-center justify-center animate-pulse">
          <span className="text-white text-sm font-semibold drop-shadow-lg">
            Dragging...
          </span>
        </div>
      )}

      {/* Placed indicator badge */}
      {isPlaced && !isDragging && (
        <div className="absolute top-2 right-2 bg-blue-500 text-white text-xs px-2 py-1 rounded-full shadow-lg">
          In Layout
        </div>
      )}
    </div>
  );
}
